import { ArrowRightIcon } from "@chakra-ui/icons";
import {
  Box,
  chakra,
  HStack,
  Stack,
  StepSeparator,
  Text,
  VStack,
} from "@chakra-ui/react";
import { EventDetailConversionFunnelSectionData } from "@common/interfaces/KYDEvent";
import { stepConnectorClasses } from "@mui/material";
import {
  Area,
  Bar,
  CartesianGrid,
  ComposedChart,
  Funnel,
  FunnelChart,
  Label,
  LabelList,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  TooltipProps,
  XAxis,
  YAxis,
} from "recharts";
import EventDetailTitle from "../../../EventDetailTitle";

export const InlineText = chakra.span;

const eventsToColors = {
  pageview: "#0e5253",
  added_to_cart: "#157d7f",
  checkout_success: "#1da9ab",
};

const eventToLabel = {
  pageview: "Page View",
  added_to_cart: "Added to cart",
  checkout_success: "Order",
};

const CustomTooltip = ({
  labelPrefix,
  valueSuffix,
  active,
  payload,
}: TooltipProps<number, string> & {
  labelPrefix?: string;
  valueSuffix?: string;
}) => {
  if (active) {
    const label = eventToLabel[payload?.at(0)?.payload?.name] || "";
    return (
      <Stack
        p={3}
        spacing={1}
        borderColor={"gray.300"}
        borderWidth={"1px"}
        rounded={"md"}
        bg="white"
      >
        <Text fontSize={"xs"} fontWeight={"bold"}>
          {labelPrefix}
          {label}
        </Text>
        <Text fontSize="xs">
          {payload?.at(0)?.payload?.count}
          {valueSuffix}
        </Text>
        <Text>{payload?.at(0)?.value?.toFixed(2)}%</Text>
      </Stack>
    );
  }

  return null;
};

const ConversionSection = ({
  data,
}: {
  data: EventDetailConversionFunnelSectionData;
}) => {
  const funnelData = data.conversion_funnel?.steps?.map((step) => ({
    value: step.rate,
    value_display: step.rate_display,
    name: step.label,
    count: step.count_display,
    fill: eventsToColors[step.label] || "#333",
  }));

  const totalConversionRate =
    data.conversion_funnel?.steps?.at(-1)?.rate_display;
  return (
    <VStack w="full">
      <Stack direction={"column"} w="100%">
        <EventDetailTitle>Conversion Funnel</EventDetailTitle>
      </Stack>
      <Stack w="full" h={["600", "400"]} direction={["column", "row"]}>
        <VStack h={"full"} justifyContent={"center"}>
          <VStack width={"full"}>
            <Text fontWeight={"bold"} textAlign={"center"} w="full">
              Conversion Rate
            </Text>
            <Text
              fontSize={"xxx-large"}
              fontWeight="bold"
              textAlign={"center"}
              width={"full"}
            >
              {totalConversionRate}
            </Text>
          </VStack>
        </VStack>
        <ResponsiveContainer height={400}>
          <ComposedChart
            width={730}
            height={200}
            data={funnelData}
            margin={{
              top: 20,
            }}
          >
            <XAxis
              dataKey="name"
              fontSize={10}
              strokeOpacity={0.5}
              tickFormatter={(value) => {
                return eventToLabel[value] || value;
              }}
            />
            <YAxis
              strokeOpacity={0.5}
              tickFormatter={(value, index) => `${(value * 100).toFixed(2)}%`}
              fontSize={10}
            />
            <Tooltip
              cursor={{ fill: "#eee" }}
              labelStyle={{ color: "black" }}
              //@ts-ignore
              content={<CustomTooltip valueSuffix=" events" />}
            />
            <Bar dataKey="value" barSize={100} fill="#413ea0">
              <LabelList
                dataKey="value_display"
                position="top"
                fill="#888"
                fontSize={12}
                fontWeight={"bold"}
              />
            </Bar>
          </ComposedChart>
        </ResponsiveContainer>
      </Stack>
    </VStack>
  );
};

export default ConversionSection;
